import React from "react";
import { Lock, PlugZap, Info } from "lucide-react";

// Reusable EMPTY, connect-ready dashboard shell. Intentionally shows NO data —
// it waits for a real provider (GSC, SerpApi, etc.) to be authorized before any
// metrics appear. Never fabricate ranking/competitor/AI-visibility numbers.
export default function ConnectReadyDashboard({ icon: Icon, testid, title, subtitle, providers = [], metrics = [], note }) {
  return (
    <div data-testid={testid}>
      <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
        {Icon && <Icon className="h-6 w-6 text-launch" />} {title}
      </h1>
      <p className="text-slate-500 mt-1 mb-6 max-w-3xl">{subtitle}</p>

      {/* Not-connected banner */}
      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 mb-6" data-testid={`${testid}-connect`}>
        <div className="flex items-start gap-4">
          <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-navy/5 text-navy shrink-0"><PlugZap className="h-5 w-5" /></span>
          <div className="flex-1">
            <p className="font-bold text-navy">No data source connected yet</p>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              This dashboard is ready to go. Connect one of the providers below to start pulling live data. Until a provider is authorized, we intentionally show no numbers here — no estimates, no placeholders.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {providers.map((p) => (
                <div key={p.name} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid={`${testid}-provider-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="text-sm font-semibold text-navy">{p.name}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Not connected</span>
                  <button disabled className="text-xs font-semibold text-slate-400 border border-slate-200 rounded px-2 py-1 cursor-not-allowed" data-testid={`${testid}-connect-btn`}>Connect</button>
                </div>
              ))}
            </div>
            {providers.some((p) => p.hint) && (
              <ul className="mt-3 space-y-1">
                {providers.filter((p) => p.hint).map((p) => (
                  <li key={p.name} className="text-xs text-slate-400">{p.name}: {p.hint}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Locked metric shells */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {metrics.map((m) => (
          <div key={m.label} className="relative bg-white border border-slate-200 rounded-xl p-5 overflow-hidden" data-testid={`${testid}-metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-500">{m.label}</p>
              <Lock className="h-4 w-4 text-slate-300" />
            </div>
            <p className="mt-3 text-3xl font-extrabold text-slate-200 select-none">—</p>
            <p className="mt-1 text-xs text-slate-400">{m.desc}</p>
          </div>
        ))}
      </div>

      {note && (
        <div className="flex items-start gap-3 rounded-lg bg-navy/5 border border-navy/10 p-4">
          <Info className="h-5 w-5 text-navy/60 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600 leading-relaxed">{note}</p>
        </div>
      )}
    </div>
  );
}
